import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactECharts from 'echarts-for-react';
import { SalesFunnelData, SalesFunnelStage } from '../hooks/useOverviewData';
import { formatNumber } from '../utils/format';
import { AV2_CHART } from '../utils/chartTheme';

interface Props {
    data: SalesFunnelData | null;
    previous?: SalesFunnelData | null;
    loading: boolean;
}

const STAGE_ORDER: SalesFunnelStage['key'][] = ['measures', 'offers', 'agreements'];

const STAGE_LABEL: Record<SalesFunnelStage['key'], { id: string; defaultMessage: string }> = {
    measures: { id: 'analyticsV2.salesFunnel.measures', defaultMessage: 'Measures' },
    offers: { id: 'analyticsV2.salesFunnel.offers', defaultMessage: 'Offers' },
    agreements: { id: 'analyticsV2.salesFunnel.agreements', defaultMessage: 'Agreements' },
};

const COLOR_CURRENT = '#1e3a5f';
const COLOR_PREVIOUS = '#cbd5e1';

const SalesFunnelCard: React.FC<Props> = ({ data, previous, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const curByKey = useMemo(() => {
        const m = new Map<SalesFunnelStage['key'], number>();
        (data?.stages ?? []).forEach((s) => m.set(s.key, s.count));
        return m;
    }, [data]);

    const prevByKey = useMemo(() => {
        const m = new Map<SalesFunnelStage['key'], number>();
        (previous?.stages ?? []).forEach((s) => m.set(s.key, s.count));
        return m;
    }, [previous]);

    const closeRate = data?.close_rate_pct ?? null;
    const closeRatePrev = previous?.close_rate_pct ?? null;
    const closeRateDelta =
        closeRate != null && closeRatePrev != null ? Math.round((closeRate - closeRatePrev) * 10) / 10 : null;

    const isEmpty = data != null && data.stages.every((s) => s.count === 0);
    const hasPrev = previous != null && (previous.stages ?? []).some((s) => s.count > 0);

    const labels = STAGE_ORDER.map((k) => intl.formatMessage(STAGE_LABEL[k]));
    const cur = STAGE_ORDER.map((k) => curByKey.get(k) ?? 0);
    const prev = STAGE_ORDER.map((k) => prevByKey.get(k) ?? 0);
    const thisPeriodLabel = intl.formatMessage({ id: 'analyticsV2.salesFunnel.thisPeriod', defaultMessage: 'this period' });
    const prevPeriodLabel = intl.formatMessage({ id: 'analyticsV2.salesFunnel.previousPeriod', defaultMessage: 'previous' });

    const conversions = STAGE_ORDER.slice(1).map((toKey, idx) => {
        const fromKey = STAGE_ORDER[idx];
        const fromV = curByKey.get(fromKey) ?? 0;
        const toV = curByKey.get(toKey) ?? 0;
        return { fromKey, toKey, pct: fromV > 0 ? (toV / fromV) * 100 : null };
    });

    const option: any = {
        textStyle: { fontFamily: AV2_CHART.font },
        grid: { left: 2, right: 10, top: 24, bottom: hasPrev ? 28 : 2, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: '#0f172a',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: '#ffffff', fontSize: 12, fontFamily: AV2_CHART.font },
            valueFormatter: (v: number) => formatNumber(Number(v) || 0, locale),
        },
        legend: hasPrev
            ? {
                  bottom: 0,
                  data: [thisPeriodLabel, prevPeriodLabel],
                  icon: 'roundRect',
                  itemWidth: 11,
                  itemHeight: 11,
                  itemGap: 18,
                  textStyle: { color: '#475569', fontSize: 12, fontFamily: AV2_CHART.font },
              }
            : undefined,
        xAxis: {
            type: 'category',
            data: labels,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#e2e8f0' } },
            axisLabel: { color: '#475569', fontSize: 12, fontWeight: 500, fontFamily: AV2_CHART.font },
        },
        yAxis: {
            type: 'value',
            splitNumber: 3,
            axisLabel: { color: '#94a3b8', fontSize: 11, fontFamily: AV2_CHART.font },
            splitLine: { lineStyle: { color: '#eef2f7' } },
        },
        series: [
            {
                name: thisPeriodLabel,
                type: 'bar',
                data: cur,
                barMaxWidth: 32,
                barGap: '18%',
                itemStyle: { color: COLOR_CURRENT, borderRadius: [4, 4, 0, 0] },
                label: {
                    show: true,
                    position: 'top',
                    color: '#0f172a',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: AV2_CHART.font,
                    formatter: (p: any) => formatNumber(Number(p.value) || 0, locale),
                },
            },
            ...(hasPrev
                ? [
                      {
                          name: prevPeriodLabel,
                          type: 'bar',
                          data: prev,
                          barMaxWidth: 32,
                          itemStyle: { color: COLOR_PREVIOUS, borderRadius: [4, 4, 0, 0] },
                      },
                  ]
                : []),
        ],
    };

    return (
        <section className="av2-card av2-sales">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.salesFunnel.title" defaultMessage="Sales funnel" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.salesFunnel.subtitle"
                            defaultMessage="Measure → Offer → Agreement, in the selected period"
                        />
                    </p>
                </div>
                {closeRate != null && (
                    <div className="av2-sales__close-rate">
                        <span className="av2-sales__close-rate-label">
                            <FormattedMessage id="analyticsV2.salesFunnel.closeRateLabel" defaultMessage="Close rate" />
                        </span>
                        <span className="av2-sales__close-rate-value">{formatRate(closeRate, locale)}%</span>
                        {closeRateDelta != null && Math.abs(closeRateDelta) >= 0.1 && (
                            <span
                                className={`av2-sales__close-rate-delta av2-sales__close-rate-delta--${
                                    closeRateDelta > 0 ? 'up' : 'down'
                                }`}
                            >
                                {closeRateDelta > 0 ? '↑' : '↓'} {formatRate(Math.abs(closeRateDelta), locale)}
                                <FormattedMessage id="analyticsV2.cashflow.ppSuffix" defaultMessage=" pp" />
                            </span>
                        )}
                    </div>
                )}
            </div>
            <div className="av2-card__body">
                {loading || !data ? (
                    <SalesFunnelSkeleton />
                ) : isEmpty ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.salesFunnel.empty"
                            defaultMessage="No measures, offers, or agreements in this period."
                        />
                    </div>
                ) : (
                    <>
                        <div className="av2-sales__chart">
                            <ReactECharts
                                option={option}
                                notMerge
                                style={{ height: 300, width: '100%' }}
                                opts={{ renderer: 'svg' }}
                            />
                        </div>
                        <div className="av2-sales__conv">
                            {conversions.map((c) => (
                                <div className="av2-sales__conv-chip" key={c.fromKey}>
                                    <span className="av2-sales__conv-pair">
                                        <FormattedMessage
                                            id="analyticsV2.salesFunnel.conversionPair"
                                            defaultMessage="{from} → {to}"
                                            values={{
                                                from: intl.formatMessage(STAGE_LABEL[c.fromKey]),
                                                to: intl.formatMessage(STAGE_LABEL[c.toKey]),
                                            }}
                                        />
                                    </span>
                                    <span className="av2-sales__conv-pct">
                                        {c.pct != null ? <>{formatRate(c.pct, locale)}%</> : '—'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

const SalesFunnelSkeleton: React.FC = () => (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: 12, height: 240, padding: '12px 0' }}>
        {[100, 64, 40].map((h, i) => (
            <div key={i} className="av2-skel" style={{ width: 32, height: `${h}%`, borderRadius: 'var(--av2-radius-md)' }} />
        ))}
    </div>
);

const formatRate = (n: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(n);

export default SalesFunnelCard;
